import {
    useQueries,
    useQuery,
    UseQueryOptions,
    UseQueryResult,
} from '@tanstack/react-query'
import { QueryKeys } from 'constants/query-keys'
import { BaseFetchProps, OperationsMetricsDetailProps } from 'types/api'
import { TIME } from 'utils/misc'
import { useEssentials } from 'hooks/utils/useEssentials'
import { useAnalytics } from 'utils/analytics'
import {
    fetchDimensions,
    fetchOperationsMetricsDetails,
} from 'queries/operations/dimensions'
import { isCompany, isStore } from 'utils/location'
import { getGreenfieldLocationFilter } from 'utils/greenfield'
import { TableData } from 'utils/data'

export type MetricDetail = {
    metric_id: number
    metric_name: string
    page_type: string
    card_id_non_store: number
    card_id_store: number
    stack_rank: number
    time_period: string
    location_api_identifier: string
}

export type TimeDimension =
    | 'Prior Day'
    | 'Week to Day'
    | 'Prior Week'
    | 'Month to Day'
    | 'Prior Month'
    | 'Year to Day'

export type WithTimeDimension = {
    time_dimension: TimeDimension
}

export type UseDimensionDetailResponse = MetricDetail &
    WithTimeDimension & {
        tile: TableData
        isLoading: boolean
        isError: boolean
        error: Error | null
        refetch: UseQueryResult['refetch']
    }

export function useDimensions({ env, accessToken, filters }: BaseFetchProps) {
    const { currentLocation } = useEssentials()
    const { trackEvent } = useAnalytics()

    const { tenMinutes } = TIME

    return useQuery<MetricDetail[]>(
        [QueryKeys.OPERATIONS_DIMENSIONS, currentLocation],
        () =>
            fetchDimensions({
                env,
                accessToken,
                filters,
                trackEvent,
            }),
        {
            staleTime: tenMinutes,
            cacheTime: tenMinutes,
            retry: false,
        },
    )
}

export function useDimensionDetails({
    env,
    accessToken,
    location,
    filters,
    tiles,
}: OperationsMetricsDetailProps): UseDimensionDetailResponse[] {
    const { tenMinutes } = TIME
    const { trackEvent } = useAnalytics()
    const isCompanyLocation = isCompany(location)
    // Convert timeDimension to lowercase with underscores
    const formattedTimeDimension = (tile: MetricDetail & WithTimeDimension) =>
        tile.time_dimension.toLowerCase().replace(/\s+/g, '_')

    const greenfieldFilters = (tile: MetricDetail & WithTimeDimension) => ({
        ...(!isCompanyLocation && getGreenfieldLocationFilter(location)),
        time_dimension: formattedTimeDimension(tile), // Use the formatted time dimension
    })

    // Map over details to create queries
    const queries: UseQueryOptions<TableData, Error>[] = tiles.map((tile) => ({
        queryKey: [
            QueryKeys.OPERATIONSMETRICSDETAILS,
            location,
            tile,
            filters,
            formattedTimeDimension(tile),
        ],
        queryFn: () =>
            fetchOperationsMetricsDetails({
                env,
                accessToken,
                dynamicSubPath: isStore(location)
                    ? tile.card_id_store.toString()
                    : tile.card_id_non_store.toString(),
                method: 'GET',
                filters: greenfieldFilters(tile),
                trackEvent,
            }),
        staleTime: tenMinutes,
        cacheTime: tenMinutes,
        retry: false,
    }))

    // Execute all queries in parallel
    const results = useQueries({ queries })

    // Combine each detail object with its corresponding result and add states
    return results.map((result, index) => ({
        ...tiles[index],
        tile: result.data ?? { rows: [] },
        isLoading: result.isLoading,
        isError: result.isError,
        error: result.error,
        refetch: result.refetch,
    }))
}
